import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import * as moment from 'moment';
import { DataService } from '../../../shared/services';

export interface PmRouteReportEntry {
  id: number;
  location_name: string;
  asset_full_name: string;
  task_component: string;
  task_description: string;
  issue_comments?: string;
  measured_value?: string;
  measure_type?: 'value' | 'text' | 'photo' | null;
  value_numeric?: number | null;
  value_text?: string | null;
  skip_reason?: string;
  skip_comments?: string;
  has_images?: boolean;
}

export interface PmRouteReport {
  id: number;
  plan_id: number;
  plan_name: string;
  user_name: string;
  created_at: string;
  issues: PmRouteReportEntry[];
  measurements: PmRouteReportEntry[];
  skipped_assets: PmRouteReportEntry[];
  isOpen?: boolean;
}

export type PmRouteFeedbackType = 'issue' | 'measurement' | 'skipped_asset';

export interface PmRouteFeedbackEntry extends PmRouteReportEntry {
  type: PmRouteFeedbackType;
  created_at: string;
  user_name: string;
  plan_name: string;
}

@Component({
  selector: 'app-pm-route-data',
  templateUrl: './pm-route-data.component.html',
  styleUrls: ['./pm-route-data.component.scss'],
})
export class PmRouteDataComponent implements OnChanges {
  @Input() planId = 0;
  @Input() assetId = 0;
  @Input() embedded = false;

  reports: PmRouteReport[] = [];
  flatEntries: PmRouteFeedbackEntry[] = [];
  isLoading = false;

  constructor(private data: DataService) {}

  get isFlatView(): boolean {
    return this.planId <= 0 && this.assetId > 0;
  }

  get hasEntries(): boolean {
    return this.isFlatView ? this.flatEntries.length > 0 : this.reports.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planId'] || changes['assetId']) {
      void this.loadReports();
    }
  }

  async loadReports(): Promise<void> {
    const url = this.resolveApiUrl();
    if (!url) {
      this.reports = [];
      this.flatEntries = [];
      return;
    }

    this.isLoading = true;
    try {
      const data: PmRouteReport[] = await this.data.get(url).toPromise();
      this.reports = data.map((report) => ({
        ...report,
        isOpen: data.length === 1,
      }));
      this.flatEntries = this.buildFlatEntries(this.reports);
    } catch {
      this.reports = [];
      this.flatEntries = [];
    } finally {
      this.isLoading = false;
    }
  }

  private buildFlatEntries(reports: PmRouteReport[]): PmRouteFeedbackEntry[] {
    const entries: PmRouteFeedbackEntry[] = [];
    const addEntries = (
      items: PmRouteReportEntry[],
      type: PmRouteFeedbackType,
      report: PmRouteReport,
    ) => {
      for (const item of items) {
        entries.push({
          ...item,
          type,
          created_at: report.created_at,
          user_name: report.user_name,
          plan_name: report.plan_name,
        });
      }
    };

    for (const report of reports) {
      addEntries(report.issues, 'issue', report);
      addEntries(report.measurements, 'measurement', report);
      addEntries(report.skipped_assets, 'skipped_asset', report);
    }

    return entries.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }

  badgeClass(type: PmRouteFeedbackType): string {
    switch (type) {
      case 'issue':
        return 'badge-warning';
      case 'measurement':
        return 'badge-primary';
      case 'skipped_asset':
        return 'badge-secondary';
    }
  }

  private resolveApiUrl(): string | null {
    if (this.planId > 0) {
      return `api/plans/pm-route-data/${this.planId}`;
    }
    if (this.assetId > 0) {
      return `api/assets/pm-route-data/${this.assetId}`;
    }
    return null;
  }

  reportNumber(index: number): number {
    return this.reports.length - index;
  }

  toggleReport(index: number): void {
    this.reports[index].isOpen = !this.reports[index].isOpen;
  }

  entryCount(report: PmRouteReport): number {
    return (
      report.issues.length +
      report.measurements.length +
      report.skipped_assets.length
    );
  }

  formatCreatedAt(createdAt: string): string {
    return moment.utc(createdAt).local().format('DD/MM/YYYY HH:mm');
  }
}
